import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { taskAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { PageLayout } from '../layout';
import { Card, Badge, AvatarGroup, Spinner, NavIcon, PriorityIcon } from '../common';
import { STATUS_CONFIG, PRIORITY_CONFIG, formatDate, isOverdue } from '../../utils/constants';
import { TaskDetail } from '../tasks/TaskDetail';
import { ManagerProductivitySection } from './ManagerProductivitySection';
import { getCachedDashboard, setCachedDashboard, getInFlightDashboardPromise, invalidateDashboardCache } from '../../utils/dashboardCache';
import toast from 'react-hot-toast';

const StatCard = ({ label, value, icon, color, onClick }) => (
  <Card 
    onClick={onClick}
    className="p-5 hover:-translate-y-1 hover:shadow-md hover:shadow-brand-500/[0.02] transition-all duration-300 border border-slate-200/60 dark:border-slate-800/80 hover:border-brand-300 dark:hover:border-brand-850 group cursor-pointer active:scale-98"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1">{label}</p>
        <p className="text-3xl font-extrabold text-slate-900 dark:text-white font-display leading-none">{value}</p>
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${color}`}>{icon}</div>
    </div>
  </Card>
);

const PIE_COLORS = { todo: '#94a3b8', in_progress: '#6366f1', completed: '#10b981', on_hold: '#d97706', reopened: '#f97316' };

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border border-slate-100/50 dark:border-slate-800/80 rounded-xl px-3 py-2 shadow-lg text-xs animate-scale-in">
        {label && <p className="font-bold text-slate-800 dark:text-white mb-1">{label}</p>}
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.fill || p.color }} className="font-semibold">
            {p.name}: {p.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const DashboardTasksModal = ({ type, onClose, onSelectTask }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchTasks = useCallback(async (pageNum, isReset = false) => {
    setLoading(true);
    try {
      const params = { page: pageNum, limit: 10 };
      if (type === 'pending') {
        params.status = 'todo';
      } else if (type === 'in_progress') {
        params.status = 'in_progress';
      } else if (type === 'completed') {
        params.status = 'completed';
      } else if (type === 'overdue') {
        params.overdue = 'true';
      }

      const { data } = await taskAPI.getAll(params);
      const newTasks = data.data.tasks || [];
      const pg = data.data.pagination || {};
      const totalPages = pg.pages || 1;

      setTasks(prev => isReset ? newTasks : [...prev, ...newTasks]);
      setHasMore(pageNum < totalPages);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load tasks list');
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    setPage(1);
    setTasks([]);
    setHasMore(true);
    fetchTasks(1, true);
  }, [type]);

  useEffect(() => {
    if (page > 1) {
      fetchTasks(page, false);
    }
  }, [page, fetchTasks]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 10) {
      if (!loading && hasMore) {
        setPage(p => p + 1);
      }
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'total': return 'Total Tasks';
      case 'pending': return 'Pending Tasks';
      case 'in_progress': return 'In Progress Tasks';
      case 'completed': return 'Completed Tasks';
      case 'overdue': return 'Overdue Tasks';
      default: return 'Tasks';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-[4px] animate-fade-in">
      <div className="bg-white dark:bg-slate-900 rounded-3xl w-full max-w-2xl max-h-[75vh] flex flex-col shadow-2xl border border-slate-100 dark:border-slate-800/80 overflow-hidden animate-scale-in">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800/40 flex items-center justify-between bg-slate-50/50 dark:bg-slate-900 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-brand-500 animate-pulse" />
            <h3 className="font-bold text-slate-900 dark:text-white font-display text-base">{getTitle()}</h3>
          </div>
          <button 
            onClick={onClose} 
            className="p-1.5 rounded-xl hover:bg-slate-150 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-650 transition-all"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div 
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-6 min-h-[200px]"
        >
          {tasks.length === 0 && loading ? (
            <div className="h-full flex items-center justify-center py-10"><Spinner size="lg" /></div>
          ) : tasks.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-slate-405 py-10">
              <NavIcon name="ClipboardDocumentListIcon" className="w-12 h-12 text-slate-200 dark:text-slate-700 mb-3" />
              <p className="text-sm font-semibold">No tasks found</p>
              <p className="text-xs text-slate-400 mt-1">There are no tasks matching this filter.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50 dark:divide-slate-800/20">
              {tasks.map((task) => (
                <div 
                  key={task._id} 
                  onClick={() => {
                    onClose();
                    if (onSelectTask) {
                      onSelectTask(task._id);
                    } else {
                      navigate(`/tasks?highlight=${task._id}`);
                    }
                  }}
                  className="py-3 flex items-center justify-between gap-4 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 px-3 -mx-3 rounded-2xl cursor-pointer transition-all duration-150 group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-1.5 h-1.5 rounded-full ${STATUS_CONFIG[task.status]?.dot}`} />
                      <p className="text-sm font-semibold text-slate-800 dark:text-white truncate group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                        {task.title}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={STATUS_CONFIG[task.status]?.color}>{STATUS_CONFIG[task.status]?.label}</Badge>
                      <Badge className={PRIORITY_CONFIG[task.priority]?.color}>
                        <PriorityIcon iconName={PRIORITY_CONFIG[task.priority]?.iconName} className="w-3 h-3" />
                        <span className="ml-1">{PRIORITY_CONFIG[task.priority]?.label}</span>
                      </Badge>
                      {task.departmentId && (
                        <span className="text-[10px] text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-800 rounded-full px-2 py-0.5">
                          {task.departmentId.name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {task.assignedTo?.length > 0 && <AvatarGroup users={task.assignedTo} max={2} />}
                    <span className="text-xs text-slate-400 font-medium">{formatDate(task.deadline)}</span>
                  </div>
                </div>
              ))}
              
              {/* Bottom Loading Indicator */}
              {loading && (
                <div className="flex justify-center py-4">
                  <Spinner size="sm" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(() => getCachedDashboard(user));
  const [loading, setLoading] = useState(() => !getCachedDashboard(user) && !getInFlightDashboardPromise());
  const [refreshing, setRefreshing] = useState(false);
  const [activeModalType, setActiveModalType] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  const fetchStats = useCallback(async (force = false) => {
    if (!force) {
      const currentCache = getCachedDashboard(user);
      if (currentCache) {
        setData(currentCache);
        setLoading(false);
        setRefreshing(false);
        return;
      }
      const inFlight = getInFlightDashboardPromise();
      if (inFlight) {
        setLoading(true);
        try {
          const res = await inFlight;
          setData(res);
        } catch (e) {
          try {
            const fallback = await taskAPI.dashboard();
            const fallbackData = fallback.data?.data || fallback.data;
            setCachedDashboard(user, fallbackData);
            setData(fallbackData);
          } catch (fbErr) {
            console.error(fbErr);
          }
        } finally {
          setLoading(false);
          setRefreshing(false);
        }
        return;
      }
    }

    if (force) {
      setRefreshing(true);
      invalidateDashboardCache();
    } else {
      setLoading(true);
    }

    try {
      const { data: res } = await taskAPI.dashboard();
      const resData = res.data?.data || res.data;
      setCachedDashboard(user, resData);
      setData(resData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user]);

  useEffect(() => {
    const cached = getCachedDashboard(user);
    if (!cached) {
      fetchStats(false);
    }
  }, [fetchStats, user]);

  if (loading) return (
    <PageLayout title="Dashboard">
      <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
    </PageLayout>
  );

  const { stats, recentTasks } = data || {};

  const pieData = stats ? Object.entries(stats.byStatus).map(([k, v]) => ({
    name: STATUS_CONFIG[k]?.label || k, value: v, key: k
  })).filter(d => d.value > 0) : [];

  const barData = stats ? Object.entries(stats.byPriority).map(([k, v]) => ({
    name: PRIORITY_CONFIG[k]?.label || k, count: v,
    fill: k === 'urgent' ? '#ef4444' : k === 'high' ? '#f97316' : k === 'medium' ? '#6366f1' : '#94a3b8'
  })) : [];

  const greeting = `Good ${new Date().getHours() < 12 ? 'morning' : 'afternoon'}, ${user?.name?.split(' ')[0]}`;

  return (
    <PageLayout
      title={greeting}
      subtitle="Here's what's happening in your workspace today"
      actions={
        <button
          onClick={() => fetchStats(true)}
          disabled={refreshing}
          title="Refresh Dashboard"
          className="p-2 rounded-xl text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
        >
          <NavIcon name="ArrowPathIcon" className={`w-5 h-5 ${refreshing ? 'animate-spin text-brand-600' : ''}`} />
        </button>
      }
    >
      <div className="animate-slide-up space-y-6">
        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          <StatCard label="Total Tasks" value={stats?.total || 0} icon={<NavIcon name="ClipboardDocumentListIcon" className="w-5 h-5" />} color="bg-brand-50 text-brand-600 dark:bg-brand-950/30" onClick={() => setActiveModalType('total')} />
          <StatCard label="Pending" value={stats?.byStatus?.todo || 0} icon={<NavIcon name="ListBulletIcon" className="w-5 h-5" />} color="bg-amber-50 text-amber-600 dark:bg-amber-950/30" onClick={() => setActiveModalType('pending')} />
          <StatCard label="In Progress" value={stats?.byStatus?.in_progress || 0} icon={<NavIcon name="ClockIcon" className="w-5 h-5" />} color="bg-blue-50 text-blue-600 dark:bg-blue-950/30" onClick={() => setActiveModalType('in_progress')} />
          <StatCard label="Completed" value={stats?.byStatus?.completed || 0} icon={<NavIcon name="CheckCircleIcon" className="w-5 h-5" />} color="bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30" onClick={() => setActiveModalType('completed')} />
          <StatCard label="Overdue" value={stats?.overdue || 0} icon={<NavIcon name="ExclamationTriangleIcon" className="w-5 h-5" />} color="bg-red-50 text-red-600 dark:bg-red-950/30" onClick={() => setActiveModalType('overdue')} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Status Pie */}
          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">By Status</h3>
            {pieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={52} outerRadius={75} dataKey="value" paddingAngle={6}>
                      {pieData.map((entry) => <Cell key={entry.key} fill={PIE_COLORS[entry.key]} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 mt-2">
                  {pieData.map((d) => (
                    <div key={d.key} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE_COLORS[d.key] }} />
                        <span className="text-slate-600 dark:text-slate-400">{d.name}</span>
                      </div>
                      <span className="font-semibold text-slate-900 dark:text-white">{d.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : <p className="text-sm text-slate-400 text-center py-8">No tasks yet</p>}
          </Card>

          {/* Priority Bar */}
          <Card className="p-5 col-span-2">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">Tasks by Priority</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={barData} barSize={36} margin={{ left: -10, right: 10, top: 10, bottom: 5 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(99, 102, 241, 0.04)', radius: 6 }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {barData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>

        {/* Manager Productivity & Approval TAT Hub (Admin Only) */}
        <ManagerProductivitySection />

        {/* Recent Tasks */}
        <Card>
          <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Recent Tasks</h3>
            <button onClick={() => navigate('/tasks')} className="text-xs text-brand-600 hover:underline">View all</button>
          </div>
          <div className="divide-y divide-slate-50 dark:divide-slate-800/40">
            {recentTasks?.length > 0 ? recentTasks.map((task) => (
              <div
                key={task._id}
                onClick={() => setSelectedTaskId(task._id)}
                className="px-5 py-3.5 flex items-center gap-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{task.title}</p>
                    {isOverdue(task.deadline, task.status) && (
                      <span className="text-xs text-red-500 font-medium flex-shrink-0 flex items-center gap-1">
                        <NavIcon name="ExclamationTriangleIcon" className="w-3 h-3 text-red-500" />
                        <span>Overdue</span>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={STATUS_CONFIG[task.status]?.color} dot={STATUS_CONFIG[task.status]?.dot}>
                      {STATUS_CONFIG[task.status]?.label}
                    </Badge>
                    <Badge className={PRIORITY_CONFIG[task.priority]?.color}>
                      <div className="flex items-center gap-1">
                        <PriorityIcon iconName={PRIORITY_CONFIG[task.priority]?.iconName} className="w-3 h-3" />
                        <span>{PRIORITY_CONFIG[task.priority]?.label}</span>
                      </div>
                    </Badge>
                    {task.departmentId && (
                      <span className="text-xs text-slate-405 dark:text-slate-500">{task.departmentId.name}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {task.assignedTo?.length > 0 && <AvatarGroup users={task.assignedTo} max={3} />}
                  <span className="text-xs text-slate-400">{formatDate(task.deadline)}</span>
                </div>
              </div>
            )) : (
              <div className="px-5 py-10 text-center text-sm text-slate-400">No tasks yet. Create your first one!</div>
            )}
          </div>
        </Card>
      </div>

      {activeModalType && (
        <DashboardTasksModal 
          type={activeModalType} 
          onClose={() => setActiveModalType(null)} 
          onSelectTask={(id) => setSelectedTaskId(id)}
        />
      )}

      {selectedTaskId && (
        <TaskDetail
          taskId={selectedTaskId}
          onClose={() => setSelectedTaskId(null)}
          onUpdated={() => fetchStats(true)}
        />
      )}
    </PageLayout>
  );
};
